import OptimizedImage from "@components/ui/OptimizedImage"
import { useRoutePageContext } from "@components/ui/route-page-context"
import { imageAlt, imageRenderProps, imageTitle } from "@lib/component-utils"
import type { WorkProductItem } from "@site-types/content"
import useEmblaCarousel from "embla-carousel-react"
import { useCallback, useEffect, useState } from "react"

type GridGalleryProps = {
  products: WorkProductItem[]
}

export function GridGallery({ products }: GridGalleryProps) {
  const { locale } = useRoutePageContext()
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [canScrollPrev, setCanScrollPrev] = useState(false)
  const [canScrollNext, setCanScrollNext] = useState(false)
  const [viewportRef, emblaApi] = useEmblaCarousel({
    align: "start",
    breakpoints: {
      "(min-width: 600px)": { active: false },
    },
    containScroll: "trimSnaps",
    dragFree: false,
    loop: false,
  })

  const updateCarouselState = useCallback(() => {
    if (!emblaApi) return

    setSelectedIndex(emblaApi.selectedScrollSnap())
    setCanScrollPrev(emblaApi.canScrollPrev())
    setCanScrollNext(emblaApi.canScrollNext())
  }, [emblaApi])

  useEffect(() => {
    if (!emblaApi) return

    updateCarouselState()
    emblaApi.on("select", updateCarouselState)
    emblaApi.on("settle", updateCarouselState)
    emblaApi.on("reInit", updateCarouselState)

    return () => {
      emblaApi.off("select", updateCarouselState)
      emblaApi.off("settle", updateCarouselState)
      emblaApi.off("reInit", updateCarouselState)
    }
  }, [emblaApi, updateCarouselState])

  const scrollPrev = () => {
    emblaApi?.scrollPrev()
  }

  const scrollNext = () => {
    emblaApi?.scrollNext()
  }

  const scrollToProduct = (index: number) => {
    emblaApi?.scrollTo(index)
  }

  return (
    <div
      className="pr-[1em] pb-[20px] [--work-mobile-gallery-control-gap:0.5rem] [--work-mobile-gallery-gap:1.25rem] [--work-mobile-gallery-shadow-space:18px] [--work-mobile-gallery-slide-size:80%] [--work-mobile-gallery-tags-gap:1rem] max-sm:pr-0 max-sm:pb-[var(--work-mobile-gallery-tags-gap)]"
      data-id="grid-gallery"
      id="grid-gallery"
    >
      <div
        data-id="grid-gallery-viewport"
        className="overflow-hidden sm:overflow-visible"
        ref={viewportRef}
      >
        <ul
          data-id="grid-gallery-container"
          className="flex list-none gap-x-[var(--work-mobile-gallery-gap)] pr-[var(--work-mobile-gallery-shadow-space)] pb-[20px] [touch-action:pan-y_pinch-zoom] max-sm:pb-0 sm:grid sm:grid-cols-2 sm:gap-[2em] md:grid-cols-3 lg:grid-cols-4 lg:gap-[3rem]"
        >
          {products.map((entry, index) => {
            const coverImageAlt = entry.coverImage ? imageAlt(entry.coverImage, locale) : ""
            const linkLabel = coverImageAlt || entry.title || "Open product"

            return (
              <li
                className="mb-[var(--work-mobile-gallery-shadow-space)] aspect-video min-h-[120px] flex-[0_0_var(--work-mobile-gallery-slide-size)] list-none border-4 border-[var(--white)] bg-[var(--green)] leading-[0] shadow-[10px_10px_0_var(--blue-50)] first:mt-0 last:mb-0 hover:scale-[0.98] hover:cursor-pointer hover:shadow-[4px_4px_0_var(--blue-50)] hover:transition hover:duration-100 hover:ease-in-out sm:m-0 sm:min-h-[unset]"
                data-id={`grid-gallery-item-${entry.id ?? index}`}
                data-testid="grid-gallery-slide"
                key={entry.id ?? index}
              >
                <a
                  className="block h-full w-full"
                  aria-label={linkLabel}
                  data-id={`grid-gallery-link-${entry.id ?? index}`}
                  href={entry.url ?? "#"}
                  target="_blank"
                  rel="noreferrer"
                  onFocus={() => scrollToProduct(index)}
                >
                  {entry.coverImage ? (
                    <OptimizedImage
                      className="h-full w-full cursor-pointer object-cover"
                      title={imageTitle(entry.coverImage, locale)}
                      alt={coverImageAlt}
                      {...imageRenderProps(entry.coverImage, "workCover")}
                    />
                  ) : (
                    <span>{entry.title}</span>
                  )}
                </a>
              </li>
            )
          })}
        </ul>
      </div>
      <div
        data-id="grid-gallery-controls"
        className="mt-[var(--work-mobile-gallery-control-gap)] flex items-center justify-center gap-[0.75rem] sm:hidden"
        aria-label="Product gallery controls"
      >
        <button
          className="grid h-[44px] w-[44px] cursor-pointer place-items-center border-0 bg-[var(--white)] text-[0.9rem] text-[var(--deep-blue)] leading-none [clip-path:polygon(0_0,calc(100%-7.5px)_0,100%_7.5px,100%_100%,7.5px_100%,0_calc(100%-7.5px))] disabled:cursor-default disabled:opacity-[0.35]"
          data-id="grid-gallery-prev"
          type="button"
          onClick={scrollPrev}
          disabled={!canScrollPrev}
          aria-label="Previous product"
        >
          <i className="fa-sharp fa-solid fa-chevron-left" />
        </button>
        <div
          className="font-bold text-[1rem] text-[var(--white)] leading-none"
          data-id="product-gallery-status"
          data-testid="product-gallery-status"
          aria-live="polite"
        >
          {products.length > 0 ? selectedIndex + 1 : 0} / {products.length}
        </div>
        <button
          className="grid h-[44px] w-[44px] cursor-pointer place-items-center border-0 bg-[var(--white)] text-[0.9rem] text-[var(--deep-blue)] leading-none [clip-path:polygon(0_0,calc(100%-7.5px)_0,100%_7.5px,100%_100%,7.5px_100%,0_calc(100%-7.5px))] disabled:cursor-default disabled:opacity-[0.35]"
          data-id="grid-gallery-next"
          type="button"
          onClick={scrollNext}
          disabled={!canScrollNext}
          aria-label="Next product"
        >
          <i className="fa-sharp fa-solid fa-chevron-right" />
        </button>
      </div>
    </div>
  )
}

export default GridGallery
